import { useSelector } from 'react-redux';
import { Button, ButtonGroup, Divider } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { CustomTimeframeFilter } from 'src/components/molecules';
import { LocalFilterSection } from 'src/components/molecules/LocalFilterSection';
import { ModalContainer } from 'src/components/molecules/ModalContainer';
import { TimeframeInput } from 'src/graphql';
import { useGlobalFilters } from 'src/hooks';
import {
  FeatureFlag,
  FormState,
  GlobalFilterProps,
  InsightsDashboard,
  LocationFilterCategory,
  State,
} from 'src/types';
import { trackDashboardFiltered } from 'src/utils/analytics';
import {
  FiltersContainer,
  FiltersFooter,
  FiltersHeader,
  FiltersTitle,
  StyledCloseIcon,
} from './GlobalFilter.styles';

export interface LocationSection {
  title: string;
  options: string[];
  selectedOptions: string[];
  resetToState: string[];
  disabledOptions?: string[];
}

interface BaseProps {
  closeFilters: () => void;
  isOpen: boolean;
  dashboard: InsightsDashboard;
  type: GlobalFilterProps;
}

export interface LocationProps extends BaseProps {
  type: GlobalFilterProps.LOCATION;
  sections: LocationSection[];
  applyFilters: (selections: string[][]) => void;
  locationFilterCategory?: LocationFilterCategory;
  setLocationFilterCategory?: (category: LocationFilterCategory) => void;
  showTwoTabLayout?: boolean;
  onSelectionsChange?: (selections: string[][]) => void;
  onCrossSelect?: (
    sectionIndex: number,
    option: string,
    isSelected: boolean,
    currentSelections: string[][],
  ) => string[] | undefined;
  onAfterRegionBulkSelect?: (
    selections: string[][],
    enabledRegionNames: string[],
  ) => string[][] | undefined;
  onAfterCountryBulkSelect?: (
    selections: string[][],
    enabledCountryNames: string[],
  ) => string[][] | undefined;
}

export interface TimeProps extends BaseProps {
  type: GlobalFilterProps.TIME;
  oldestSelectableYear: number;
  customSelectedTimeframe: TimeframeInput;
  selectedTimeOption: string;
  resetToState: { option: string; customTimeframe?: TimeframeInput };
  applyFilters: (
    selectedTimeOption: string,
    customSelectedTimeframe?: TimeframeInput,
  ) => void;
}

export const GlobalFilter = (props: LocationProps | TimeProps) => {
  const {
    sectionSelections,
    customSelectedTimeframe,
    timeOptionSelected,
    customTimeState,
    applyButtonDisabled,
    timeframeYears,
    handleSectionChange,
    handleSectionSelectAll,
    handleCustomTimeFilterChange,
    handleYearSelectChange,
    clearFilters,
    segmentTrackingFilter,
  } = useGlobalFilters(props);

  const organisation = useSelector((state: State) => state.organisation);
  const features = useSelector((state: State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const locationFilterTitle = 'Location';

  const handleApplyFiltersClick = () => {
    const changingTimeFilter = props.type === GlobalFilterProps.TIME;

    changingTimeFilter
      ? props.applyFilters(
          timeOptionSelected ?? '',
          timeOptionSelected === 'custom' ? customSelectedTimeframe : undefined,
        )
      : props.applyFilters(sectionSelections);

    if (organisation.organisation && segmentEnabled) {
      trackDashboardFiltered(
        organisation.organisation.id,
        props.dashboard,
        segmentTrackingFilter,
      );
    }
  };

  const modalTitle = `Select ${
    props.type === GlobalFilterProps.TIME
      ? 'date'
      : `${locationFilterTitle.toLowerCase()}`
  }`;

  const extraPaddingIfUnderFourYears =
    timeframeYears && timeframeYears.length < 4 ? 55 : 0;

  const showTwoTabLayout =
    props.type === GlobalFilterProps.LOCATION && props.showTwoTabLayout;

  const categoryOptions: [string, string] = ['Case', 'Named Offender'];

  const selectedCategoryLabel =
    props.type === GlobalFilterProps.LOCATION &&
    props.locationFilterCategory === LocationFilterCategory.CASE
      ? 'Case'
      : 'Named Offender';

  const handleCategorySelect = (option: string) => {
    if (
      props.type === GlobalFilterProps.LOCATION &&
      props.setLocationFilterCategory
    ) {
      props.setLocationFilterCategory(
        option === 'Case'
          ? LocationFilterCategory.CASE
          : LocationFilterCategory.NAMED_OFFENDER,
      );
    }
  };

  return (
    <ModalContainer
      open={props.isOpen}
      closeModal={props.closeFilters}
      fitContent
      removePadding
      data-testid="global-filter-modal"
      title={
        <FiltersHeader>
          <StyledCloseIcon
            className="uil uil-multiply"
            onClick={props.closeFilters}
            size="16px"
          />
          <FiltersTitle>{modalTitle}</FiltersTitle>
        </FiltersHeader>
      }
      footer={
        <>
          <Divider variant="horizontal" />
          <FiltersFooter>
            <Button
              variant="secondary"
              size="medium"
              onClick={clearFilters}
              data-testid="reset-filters"
            >
              Reset filters
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleApplyFiltersClick}
              disabled={applyButtonDisabled}
              data-testid="apply-filters"
            >
              Apply filters
            </Button>
          </FiltersFooter>
        </>
      }
    >
      <FiltersContainer
        paddingBottom={
          props.type === GlobalFilterProps.TIME
            ? customTimeState === FormState.ERROR
              ? 0
              : 40
            : 20
        }
      >
        {props.type === GlobalFilterProps.LOCATION && (
          <>
            {showTwoTabLayout && (
              <div style={{ margin: '20px 0 0 0' }}>
                <ButtonGroup
                  options={categoryOptions}
                  selectedOption={selectedCategoryLabel}
                  setSelectedOption={handleCategorySelect}
                  fullWidth
                  buttonGroupOptionTestId="location-filter-category-button-group"
                />
              </div>
            )}
            {props.sections.map((section, index) => (
              <LocalFilterSection
                key={section.title}
                title={section.title}
                type="box"
                options={section.options.map((o) => ({
                  label: o,
                  disabled: section.disabledOptions?.includes(o),
                }))}
                selectedOptions={sectionSelections[index] ?? []}
                onChange={(option) => handleSectionChange(index, option)}
                onSelectAll={() => handleSectionSelectAll(index)}
                data-testid={`location-filter-section-${index}`}
              />
            ))}
          </>
        )}
        {props.type === GlobalFilterProps.TIME &&
          customSelectedTimeframe &&
          timeframeYears &&
          timeOptionSelected && (
            <div data-testid="time-filter">
              <LocalFilterSection
                title="Year"
                type="box"
                options={timeframeYears.map((o, i) => ({
                  label: o,
                  description:
                    i === timeframeYears.length - 1
                      ? 'Current year'
                      : undefined,
                }))}
                selectedOptions={
                  timeOptionSelected === 'custom' ? [] : [timeOptionSelected]
                }
                onChange={handleYearSelectChange}
                hasBottomDivider
                initialOptionsDisplaying={4}
                showNoneSelectedError={false}
                data-testid="time-filter-year-select"
                paddingBottom={extraPaddingIfUnderFourYears}
              />
              <CustomTimeframeFilter
                timeframe={customSelectedTimeframe}
                onChange={handleCustomTimeFilterChange}
                oldestSelectableYear={props.oldestSelectableYear}
                state={customTimeState}
                paddingBottom={extraPaddingIfUnderFourYears}
              />
            </div>
          )}
      </FiltersContainer>
    </ModalContainer>
  );
};
